/**
 * Clasificación y estados de emisión fiscal (AFIP / C.A.E.).
 */
import { FISCAL_SYSTEM_COPY } from './fiscalSystemCopy';

export type FiscalEmissionErrorKind = 'data_rejection' | 'transient' | 'unknown';

const PADRON_RE =
  /padr[oó]n|no est[aá] inscripto|no existe en|no se encuentra registrado|contribuyente inexistente/i;
const CUIT_DATA_RE = /cuit.*(inv[aá]lid|incorrect|no v[aá]lid)|doc\.?\s*nro.*inv[aá]lid/i;

export function classifyFiscalEmissionError(
  message: string | null | undefined
): FiscalEmissionErrorKind {
  const m = message?.trim() ?? '';
  if (!m) return 'unknown';
  if (PADRON_RE.test(m) || CUIT_DATA_RE.test(m)) return 'data_rejection';
  if (/timeout|timed out|503|502|504|conexi[oó]n|connection|econnreset|network/i.test(m)) {
    return 'transient';
  }
  return 'unknown';
}

export function isFiscalDataRejectionError(message: string | null | undefined): boolean {
  return classifyFiscalEmissionError(message) === 'data_rejection';
}

/** Mensaje corto para toast / banner (sin jerga WSFE si ya viene legible). */
export function formatFiscalEmissionErrorForUser(message: string | null | undefined): string {
  const raw = message?.trim() ?? '';
  if (!raw) {
    return FISCAL_SYSTEM_COPY.errors.defaultRejection;
  }
  if (raw.length <= 220) return raw;
  return `${raw.slice(0, 217)}…`;
}

export type FiscalEmissionUiTone = 'success' | 'warning' | 'danger' | 'neutral';

export function resolveFiscalEmissionUi(params: {
  fiscalMode?: string | null;
  fiscalStatus: string;
  invoiceStatus?: string | null;
  invoiceCae?: string | null;
  invoiceLastError?: string | null;
}): {
  displayLabel: string;
  tone: FiscalEmissionUiTone;
  isDataRejection: boolean;
  userErrorMessage: string | null;
  shouldPollForCae: boolean;
} {
  if (params.fiscalMode === 'non_fiscal') {
    return {
      displayLabel: FISCAL_SYSTEM_COPY.status.nonFiscal,
      tone: 'neutral',
      isDataRejection: false,
      userErrorMessage: null,
      shouldPollForCae: false,
    };
  }

  const inv = params.invoiceStatus?.trim();
  const hasCae = Boolean(params.invoiceCae?.trim());
  const lastError = params.invoiceLastError?.trim() || null;
  const dataRejection =
    isFiscalDataRejectionError(lastError) ||
    inv === 'error' ||
    params.fiscalStatus === 'error';

  if (inv === 'approved' || hasCae) {
    return {
      displayLabel: FISCAL_SYSTEM_COPY.status.issued,
      tone: 'success',
      isDataRejection: false,
      userErrorMessage: null,
      shouldPollForCae: false,
    };
  }

  if (dataRejection) {
    return {
      displayLabel: FISCAL_SYSTEM_COPY.status.rejected,
      tone: 'danger',
      isDataRejection: true,
      userErrorMessage: formatFiscalEmissionErrorForUser(lastError),
      shouldPollForCae: false,
    };
  }

  if (inv === 'pending_retry' || params.fiscalStatus === 'pending_retry') {
    return {
      displayLabel: FISCAL_SYSTEM_COPY.status.retryQueued,
      tone: 'warning',
      isDataRejection: false,
      userErrorMessage: lastError ? formatFiscalEmissionErrorForUser(lastError) : null,
      shouldPollForCae: true,
    };
  }

  if (inv === 'pending' || params.fiscalStatus === 'pending_sync' || params.fiscalStatus === 'pending') {
    return {
      displayLabel: FISCAL_SYSTEM_COPY.status.pendingAuthorization,
      tone: 'warning',
      isDataRejection: false,
      userErrorMessage: lastError ? formatFiscalEmissionErrorForUser(lastError) : null,
      shouldPollForCae: true,
    };
  }

  if (inv === 'error') {
    return {
      displayLabel: FISCAL_SYSTEM_COPY.status.emissionError,
      tone: 'danger',
      isDataRejection: false,
      userErrorMessage: formatFiscalEmissionErrorForUser(lastError),
      shouldPollForCae: false,
    };
  }

  return {
    displayLabel: params.fiscalStatus || '—',
    tone: 'neutral',
    isDataRejection: false,
    userErrorMessage: lastError ? formatFiscalEmissionErrorForUser(lastError) : null,
    shouldPollForCae: false,
  };
}

export function isFiscalInvoiceAwaitingCae(detail: {
  fiscalMode?: string | null;
  invoiceCae?: string | null;
  invoiceStatus?: string | null;
  fiscalStatus?: string | null;
  invoiceLastError?: string | null;
}): boolean {
  return resolveFiscalEmissionUi({
    fiscalMode: detail.fiscalMode,
    fiscalStatus: detail.fiscalStatus ?? 'pending_sync',
    invoiceStatus: detail.invoiceStatus,
    invoiceCae: detail.invoiceCae,
    invoiceLastError: detail.invoiceLastError,
  }).shouldPollForCae;
}
